import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import nds from '@nerdwallet/base-styles/classes';
import { Box, Title, Text, Link } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import {
  stringInputSpec,
  stringTextAreaSpec,
} from '../../../../lib/editing-configs';
import parseStyleFormatting from '../parse-awards-2023-formatting';
import styles from './layout-l2-investing.module.less';
import { useScrollAnimation } from '../useScrollAnimation';

const LayoutL2Investing = ({
  title,
  subtitle,
  brandLogoUrl,
  brandLogoLink,
  desktopPromoImageUrl,
  mobilePromoImageUrl,
  largeStarUrl,
  mediumStarUrl,
  smallStarUrl,
}) => {
  const desktopLargeStarRef = useRef();
  const desktopMediumStarRef = useRef();
  const desktopSmallStarRef = useRef();
  const mobileLargeStarRef = useRef();
  const mobileMediumStarRef = useRef();
  const mobileSmallStarRef = useRef();

  useScrollAnimation([
    { elementRef: desktopLargeStarRef, speed: 5 },
    { elementRef: desktopMediumStarRef, speed: 5, animationType: 'bounce' },
    { elementRef: desktopSmallStarRef, speed: 5 },

    { elementRef: mobileLargeStarRef, speed: 2 },
    { elementRef: mobileMediumStarRef, speed: 5, animationType: 'bounce' },
    { elementRef: mobileSmallStarRef, speed: 5 },
  ]);

  return (
    <Box className={styles.container}>
      <Box className={styles.brandLogo}>
        {brandLogoUrl && (
          <Box className={styles.logoWrapper}>
            {brandLogoLink ? (
              <Link href={brandLogoLink}>
                <Image
                  naturalHeight={313}
                  naturalWidth={244}
                  backgroundColor="transparent"
                  className={styles.brandLogoImage}
                  src={brandLogoUrl}
                  alt="brand logo"
                />
              </Link>
            ) : (
              <Image
                naturalHeight={313}
                naturalWidth={244}
                className={styles.brandLogoImage}
                backgroundColor="transparent"
                src={brandLogoUrl}
                alt="brand logo"
              />
            )}
          </Box>
        )}
        <Box className={styles.mobilePromoImages}>
          {largeStarUrl && (
            <Box ref={mobileLargeStarRef} className={styles.mobileLargeStar}>
              <Image
                naturalHeight={70}
                naturalWidth={70}
                backgroundColor="transparent"
                src={largeStarUrl}
                alt="large star logo"
              />
            </Box>
          )}
          {mediumStarUrl && (
            <Box ref={mobileMediumStarRef} className={styles.mobileMediumStar}>
              <Image
                naturalHeight={24}
                naturalWidth={24}
                backgroundColor="transparent"
                src={mediumStarUrl}
                alt="medium star logo"
              />
            </Box>
          )}
          {smallStarUrl && (
            <Box ref={mobileSmallStarRef} className={styles.mobileSmallStar}>
              <Image
                naturalHeight={26}
                naturalWidth={26}
                backgroundColor="transparent"
                src={smallStarUrl}
                alt="small star logo"
              />
            </Box>
          )}
          {mobilePromoImageUrl && (
            <Box className={styles.mobilePromoImage}>
              <Image
                naturalHeight={154}
                naturalWidth={222}
                backgroundColor="transparent"
                src={mobilePromoImageUrl}
                alt="mobile promo logo"
              />
            </Box>
          )}
        </Box>
      </Box>

      <Box className={styles.heroMain}>
        <Box className={styles.heroTextContainer}>
          <Box
            display="flex"
            flexDirection="column"
            className={classNames([nds('margin-vertical-3')])}
          >
            <Title className={styles.heroTitle}>
              {parseStyleFormatting(title)}
            </Title>

            <Box className={styles.heroSubtitle}>
              <Text component="p">{subtitle}</Text>
            </Box>
          </Box>
        </Box>

        <Box paddingTop="1" alignSelf="flex-end">
          <Box className={styles.desktopPromoContainer}>
            {largeStarUrl && (
              <Box
                ref={desktopLargeStarRef}
                className={styles.desktopLargeStar}
              >
                <Image
                  naturalHeight={142}
                  naturalWidth={142}
                  backgroundColor="transparent"
                  src={largeStarUrl}
                  alt="large star logo"
                />
              </Box>
            )}
            {smallStarUrl && (
              <Box
                ref={desktopSmallStarRef}
                className={styles.desktopSmallStar}
              >
                <Image
                  naturalHeight={34}
                  naturalWidth={34}
                  backgroundColor="transparent"
                  src={smallStarUrl}
                  alt="small star logo"
                />
              </Box>
            )}
            {mediumStarUrl && (
              <Box
                ref={desktopMediumStarRef}
                className={styles.desktopMediumStar}
              >
                <Image
                  naturalHeight={35}
                  naturalWidth={35}
                  backgroundColor="transparent"
                  src={mediumStarUrl}
                  alt="medium star logo"
                />
              </Box>
            )}
            {desktopPromoImageUrl && (
              <Box className={styles.desktopPromoImage}>
                <Image
                  naturalHeight={354}
                  naturalWidth={375}
                  backgroundColor="transparent"
                  src={desktopPromoImageUrl}
                  alt="desktop promo logo"
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

LayoutL2Investing.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  brandLogoUrl: PropTypes.string,
  brandLogoLink: PropTypes.string,
  desktopPromoImageUrl: PropTypes.string,
  mobilePromoImageUrl: PropTypes.string,
  largeStarUrl: PropTypes.string,
  mediumStarUrl: PropTypes.string,
  smallStarUrl: PropTypes.string,
};

LayoutL2Investing.defaultProps = {
  brandLogoUrl: '',
  brandLogoLink: '',
  desktopPromoImageUrl: '',
  mobilePromoImageUrl: '',
  largeStarUrl: '',
  mediumStarUrl: '',
  smallStarUrl: '',
};

LayoutL2Investing.getEditingConfig = () => {
  return [
    stringTextAreaSpec('title', 'Title', {
      help: 'For Gotham black font surround with a | like |this|, For italics, surround title text with ^ like ^this^. To include a linebreak, insert :br: in the textbox',
    }),
    stringTextAreaSpec('subtitle', 'Subtitle'),
    stringInputSpec('brandLogoUrl', 'Brand Logo URL'),
    stringInputSpec('brandLogoLink', 'Brand Logo Link'),
    stringInputSpec('desktopPromoImageUrl', 'Desktop Promo Image URL'),
    stringInputSpec('mobilePromoImageUrl', 'Mobile Promo Image URL'),
    stringInputSpec('largeStarUrl', 'Large Star Image URL'),
    stringInputSpec('mediumStarUrl', 'Medium Star Image URL'),
    stringInputSpec('smallStarUrl', 'Small Star Image URL'),
  ];
};

export default LayoutL2Investing;
